// actions
import * as Actions from '../actions';
import { AppState } from '../reducers';

// services
import * as Services from '../../services/StorageBrowser.Services';
import * as UploadServices from '../../services/AzureFileUpload.Services';
import * as ProtocolServices from '../../services/MedicalProtocols.Services';
import * as SettlementServices from '../../services/Settlements.Services';
import * as ProviderServices from '../../services/Provider.Services';
import * as SponsorServices from '../../services/Sponsors.Service';
import * as PractitionerServices from '../../services/Practitioners.Service';
import * as ContractServices from '../../services/Contract.Services';

// functions
import {
  contractDisplayName,
  StorageEntityRef,
  buildStorageOwnerNames,
  getEntityNameKey,
  isResolvableEntityRef,
} from '../../functions/StorageBrowser.Functions';

// models
import { UploadSasToken } from '../../models/Entities/AzureFiles/AzureFileUpload';
import {
  StorageBrowseViewModel,
  StorageFileAction,
  StorageFolderRenameViewModel,
  StorageFolderViewModel,
} from '../../models/Entities/AzureFiles/StorageBrowser';
import { buildBlobFileName, getExtension } from '../../functions/AzureFileUpload.Functions';
import { ErrorType } from '../../models/HttpError';

type KnownAction = Actions.StorageBrowser.StorageBrowserKnownAction;

/** Thunk that also returns a promise (the explorer opens/downloads the file with the resolved SAS). */
export type StorageBrowserThunk<TResult> = (
  dispatch: (action: KnownAction) => void,
  getState: () => AppState
) => Promise<TResult>;

/** Page size asked to Contracts (server accepts 1..500, default 200). */
export const STORAGE_BROWSE_PAGE_SIZE = 200;

const NotAuthorized: ErrorType = {
  ErrorCode: 401,
  ErrorMessage: 'Not authorized',
  Errors: [],
};

const toErrorType = (error: unknown): ErrorType => {
  if (typeof error === 'object' && error !== null && 'ErrorCode' in error) {
    return error as ErrorType;
  }
  return {
    ErrorCode: 0,
    ErrorMessage: error instanceof Error ? error.message : 'Unexpected error',
    Errors: [],
  };
};

export const ActionCreators = {
  /**
   * Lists the folders and files of `path` (relative to the tenant root).
   * With `continuationToken` it loads the next page and appends it. Never rejects.
   */
  BrowseStorage: (path: string, continuationToken?: string | null): StorageBrowserThunk<void> => {
    return async (dispatch, getState) => {
      const append = !!continuationToken;
      const state = getState();

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_BROWSE_FAIL_ACTION', path, append, error: NotAuthorized });
        return;
      }
      const token: string = state.oidc.user.access_token;

      dispatch({ type: 'STORAGEBROWSER_BROWSE_REQUEST_ACTION', path, append });

      try {
        const result = await Services.browseStorage(token, {
          path,
          continuationToken: continuationToken ?? null,
          pageSize: STORAGE_BROWSE_PAGE_SIZE,
        });
        dispatch({ type: 'STORAGEBROWSER_BROWSE_RECEIVE_ACTION', path, append, result });
      } catch (error: unknown) {
        dispatch({ type: 'STORAGEBROWSER_BROWSE_FAIL_ACTION', path, append, error: toErrorType(error) });
      }
    };
  },

  /**
   * Requests a read-only SAS for a file: inline to open it, attachment to download it.
   * Resolves with the SAS, rejects with ErrorType.
   */
  RequestReadSas: (path: string, action: StorageFileAction): StorageBrowserThunk<UploadSasToken> => {
    return async (dispatch, getState) => {
      const state = getState();

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_READSAS_FAIL_ACTION', path, action, error: NotAuthorized });
        throw NotAuthorized;
      }
      const token: string = state.oidc.user.access_token;

      dispatch({ type: 'STORAGEBROWSER_READSAS_REQUEST_ACTION', path, action });

      try {
        const sas = await Services.requestReadSas(token, { path, download: action === 'download' });
        dispatch({ type: 'STORAGEBROWSER_READSAS_SUCCESS_ACTION', path, action });
        return sas;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_READSAS_FAIL_ACTION', path, action, error: httpError });
        throw httpError;
      }
    };
  },

  ClearFileError: (): KnownAction => ({ type: 'STORAGEBROWSER_CLEAR_FILE_ERROR_ACTION' }),

  /**
   * Read SAS for the file preview (download=false → inline) or its "Descargar" action.
   * Does not touch the explorer pending/error state (the preview shows its own states).
   * Resolves with the SAS, rejects with ErrorType.
   */
  RequestPreviewSas: (path: string, download: boolean): StorageBrowserThunk<UploadSasToken> => {
    return async (dispatch, getState) => {
      const state = getState();

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_PREVIEWSAS_FAIL_ACTION', path, download, error: NotAuthorized });
        throw NotAuthorized;
      }
      const token: string = state.oidc.user.access_token;

      dispatch({ type: 'STORAGEBROWSER_PREVIEWSAS_REQUEST_ACTION', path, download });

      try {
        const sas = await Services.requestReadSas(token, { path, download });
        dispatch({ type: 'STORAGEBROWSER_PREVIEWSAS_SUCCESS_ACTION', path, download });
        return sas;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_PREVIEWSAS_FAIL_ACTION', path, download, error: httpError });
        throw httpError;
      }
    };
  },

  /**
   * Reads the text of a blob with its read SAS (txt/csv/json... preview), up to `maxBytes`.
   * The SAS url is never stored. Resolves with the text, rejects with ErrorType.
   */
  /**
   * Read SAS of a contract file by id (contract attachments, legacy or storage). Same actions as the preview SAS, with
   * the synthetic path `contract-file:{id}` so the explorer state is never touched.
   */
  RequestContractFileSas: (fileId: number, download: boolean): StorageBrowserThunk<UploadSasToken> => {
    return async (dispatch, getState) => {
      const state = getState();
      const path = `contract-file:${fileId}`;

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_PREVIEWSAS_FAIL_ACTION', path, download, error: NotAuthorized });
        throw NotAuthorized;
      }
      const token: string = state.oidc.user.access_token;

      dispatch({ type: 'STORAGEBROWSER_PREVIEWSAS_REQUEST_ACTION', path, download });

      try {
        const sas = await Services.requestContractFileReadSas(token, fileId, download);
        dispatch({ type: 'STORAGEBROWSER_PREVIEWSAS_SUCCESS_ACTION', path, download });
        return sas;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_PREVIEWSAS_FAIL_ACTION', path, download, error: httpError });
        throw httpError;
      }
    };
  },

  FetchPreviewText: (sasUrl: string, maxBytes: number): StorageBrowserThunk<string> => {
    return async (dispatch) => {
      dispatch({ type: 'STORAGEBROWSER_PREVIEWTEXT_REQUEST_ACTION' });
      try {
        const text = await Services.fetchBlobText(sasUrl, maxBytes);
        dispatch({ type: 'STORAGEBROWSER_PREVIEWTEXT_SUCCESS_ACTION', length: text.length });
        return text;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_PREVIEWTEXT_FAIL_ACTION', error: httpError });
        throw httpError;
      }
    };
  },

  /**
   * Resolves the display name of protocols / settlements / providers / sponsors shown by id
   * ("Protocolo 31 - PRESTO"). Names already in the store lists or already requested
   * (loading, loaded or failed) are skipped, so every id is requested at most once. Never rejects.
   */
  ResolveEntityNames: (refs: StorageEntityRef[]): StorageBrowserThunk<void> => {
    return async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;
      if (token === undefined || refs.length === 0) return;

      const known = buildStorageOwnerNames(
        state.Provider?.list,
        state.Sponsor?.list,
        state.MedicalProtocols?.list,
        state.Settlement?.list,
        undefined,
        state.Practitioners?.list,
        undefined,
        state.Contract?.list
      );
      const cache = state.StorageBrowser?.entityNames ?? {};

      const pending = refs.filter((ref) => {
        const key = getEntityNameKey(ref.kind, ref.id);
        return isResolvableEntityRef(ref) && !known[key] && !cache[key];
      });

      // Practitioners are found by person identifier (userId) in the practitioners list (same lookup as
      // GetPractitionerById): the list is read once for every pending practitioner.
      let practitionersList: Promise<{ userId: string; fullName?: string }[]> | undefined;
      const findPractitionerName = async (id: string): Promise<string | undefined> => {
        practitionersList = practitionersList ?? PractitionerServices.GetPractitioners(1, 1000, '', token);
        const list = await practitionersList;
        return list?.find((practitioner) => practitioner.userId?.toLowerCase() === id.toLowerCase())?.fullName;
      };

      await Promise.all(
        pending.map(async (ref) => {
          const key = getEntityNameKey(ref.kind, ref.id);
          const id = Number(ref.id);
          dispatch({ type: 'STORAGEBROWSER_ENTITYNAME_REQUEST_ACTION', key });
          try {
            let name: string | undefined;
            switch (ref.kind) {
              case 'protocol':
                name = (await ProtocolServices.GetProtocolById(id, token))?.title;
                break;
              case 'settlement':
                name = (await SettlementServices.GetSettlementById(id, token))?.name;
                break;
              case 'provider':
                name = (await ProviderServices.GetProvider(id, token))?.person?.businessName;
                break;
              case 'sponsor':
                name = (await SponsorServices.GetSponsor(id))?.person?.businessName;
                break;
              case 'practitioner':
                name = await findPractitionerName(ref.id);
                break;
              case 'contract':
                name = contractDisplayName(await ContractServices.GetContractById(id, token));
                break;
            }
            const trimmed = name?.trim();
            if (trimmed) {
              dispatch({ type: 'STORAGEBROWSER_ENTITYNAME_RECEIVE_ACTION', key, name: trimmed });
            } else {
              dispatch({
                type: 'STORAGEBROWSER_ENTITYNAME_FAIL_ACTION',
                key,
                error: { ErrorCode: 404, ErrorMessage: 'Name not found', Errors: [] },
              });
            }
          } catch (error: unknown) {
            dispatch({ type: 'STORAGEBROWSER_ENTITYNAME_FAIL_ACTION', key, error: toErrorType(error) });
          }
        })
      );
    };
  },

  // ---- management (contract §9.2). Every thunk rejects with ErrorType so the dialogs can show the reason. ----

  /** POST contracts/files/storage/folders (409 when it already exists). */
  CreateFolder: (parentPath: string, name: string): StorageBrowserThunk<StorageFolderViewModel> => {
    return async (dispatch, getState) => {
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_CREATEFOLDER_FAIL_ACTION', parentPath, name, error: NotAuthorized });
        throw NotAuthorized;
      }
      dispatch({ type: 'STORAGEBROWSER_CREATEFOLDER_REQUEST_ACTION', parentPath, name });
      try {
        const folder = await Services.createFolder(token, { parentPath, name });
        dispatch({ type: 'STORAGEBROWSER_CREATEFOLDER_SUCCESS_ACTION', parentPath, folder });
        return folder;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_CREATEFOLDER_FAIL_ACTION', parentPath, name, error: httpError });
        throw httpError;
      }
    };
  },

  /** POST contracts/files/storage/folders/rename (500 with the errors list on partial failure). */
  RenameFolder: (path: string, newName: string): StorageBrowserThunk<StorageFolderRenameViewModel> => {
    return async (dispatch, getState) => {
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_RENAMEFOLDER_FAIL_ACTION', path, newName, error: NotAuthorized });
        throw NotAuthorized;
      }
      dispatch({ type: 'STORAGEBROWSER_RENAMEFOLDER_REQUEST_ACTION', path, newName });
      try {
        const result = await Services.renameFolder(token, { path, newName });
        dispatch({ type: 'STORAGEBROWSER_RENAMEFOLDER_SUCCESS_ACTION', path, newName, result });
        return result;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_RENAMEFOLDER_FAIL_ACTION', path, newName, error: httpError });
        throw httpError;
      }
    };
  },

  /** DELETE contracts/files/storage/folders?path= (only empty user folders: 409 not empty, 400 system). */
  DeleteFolder: (path: string): StorageBrowserThunk<void> => {
    return async (dispatch, getState) => {
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_DELETEFOLDER_FAIL_ACTION', path, error: NotAuthorized });
        throw NotAuthorized;
      }
      dispatch({ type: 'STORAGEBROWSER_DELETEFOLDER_REQUEST_ACTION', path });
      try {
        await Services.deleteFolder(token, path);
        dispatch({ type: 'STORAGEBROWSER_DELETEFOLDER_SUCCESS_ACTION', path });
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_DELETEFOLDER_FAIL_ACTION', path, error: httpError });
        throw httpError;
      }
    };
  },

  /** DELETE contracts/files/storage/files?path= (the services mark the registrations as removed by event). */
  DeleteFile: (path: string): StorageBrowserThunk<void> => {
    return async (dispatch, getState) => {
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_DELETEFILE_FAIL_ACTION', path, error: NotAuthorized });
        throw NotAuthorized;
      }
      dispatch({ type: 'STORAGEBROWSER_DELETEFILE_REQUEST_ACTION', path });
      try {
        await Services.deleteFile(token, path);
        dispatch({ type: 'STORAGEBROWSER_DELETEFILE_SUCCESS_ACTION', path });
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_DELETEFILE_FAIL_ACTION', path, error: httpError });
        throw httpError;
      }
    };
  },

  /**
   * Uploads a file into a folder of the explorer: generic upload SAS (Contracts) + PUT straight to Azure.
   * Resolves with the blob path relative to the tenant, rejects with ErrorType.
   */
  UploadToFolder: (
    folderPath: string,
    file: File,
    onProgress?: (percent: number) => void
  ): StorageBrowserThunk<string> => {
    return async (dispatch, getState) => {
      const fileName = file.name;
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_UPLOAD_FAIL_ACTION', folderPath, fileName, error: NotAuthorized });
        throw NotAuthorized;
      }
      dispatch({ type: 'STORAGEBROWSER_UPLOAD_REQUEST_ACTION', folderPath, fileName });
      try {
        const sas = await Services.requestFolderUploadSas(token, {
          folderPath,
          fileName: buildBlobFileName(fileName),
          fileExtension: getExtension(fileName),
        });
        await UploadServices.uploadToAzure(sas.sasUrl, file, onProgress);
        dispatch({ type: 'STORAGEBROWSER_UPLOAD_SUCCESS_ACTION', folderPath, fileName, blobPath: sas.blobPath });
        return sas.blobPath;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_UPLOAD_FAIL_ACTION', folderPath, fileName, error: httpError });
        throw httpError;
      }
    };
  },

  /**
   * Lists a folder for the file picker without touching the explorer state (the picker keeps it locally).
   * Resolves with the listing, rejects with ErrorType.
   */
  BrowsePickerFolder: (path: string, continuationToken?: string | null): StorageBrowserThunk<StorageBrowseViewModel> => {
    return async (dispatch, getState) => {
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'STORAGEBROWSER_PICKERBROWSE_FAIL_ACTION', path, error: NotAuthorized });
        throw NotAuthorized;
      }
      dispatch({ type: 'STORAGEBROWSER_PICKERBROWSE_REQUEST_ACTION', path });
      try {
        const result = await Services.browseStorage(token, {
          path,
          continuationToken: continuationToken ?? null,
          pageSize: STORAGE_BROWSE_PAGE_SIZE,
        });
        dispatch({ type: 'STORAGEBROWSER_PICKERBROWSE_RECEIVE_ACTION', path });
        return result;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'STORAGEBROWSER_PICKERBROWSE_FAIL_ACTION', path, error: httpError });
        throw httpError;
      }
    };
  },
};
